import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import {
  Box,
  Container,
  Stack,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  Paper,
  Skeleton,
  useTheme,
  useMediaQuery,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import TokenFeeInfoModal from '../components/SecondHand/TokenFeeInfoModal';

const CATEGORY_OPTIONS = [
  'Electronics',
  'Fashion',
  'Home & Garden',
  'Sports',
  'Books',
  'Collectibles',
  'Other',
];

const CONDITION_CHIP_COLOR = {
  like_new: 'success',
  excellent: 'success',
  good: 'info',
  used: 'warning',
  fair: 'warning',
  poor: 'error',
};

const STATUS_META = {
  active: { label: 'Live', color: 'success', icon: '🟢' },
  pending: { label: 'Pending approval', color: 'warning', icon: '⏳' },
  expired: { label: 'Expired', color: 'error', icon: '🔻' },
  sold: { label: 'Sold', color: 'info', icon: '🏁' },
  closed: { label: 'Closed', color: 'default', icon: '⚪' },
  canceled: { label: 'Closed', color: 'default', icon: '⚪' },
};

const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

const getConditionLabel = (condition) => {
  const labels = {
    like_new: 'Like new',
    excellent: 'Excellent',
    good: 'Good',
    used: 'Used',
    fair: 'Fair',
    poor: 'Poor',
  };
  return labels[condition] || condition;
};

const getCategoryIcon = (category) => {
  const map = {
    Electronics: '📱',
    Fashion: '👗',
    'Home & Garden': '🏠',
    Sports: '⚽',
    Books: '📚',
    Toys: '🧸',
    Jewelry: '💎',
    Furniture: '🪑',
    Vehicles: '🚗',
    'Musical Instruments': '🎸',
    'Art & Collectibles': '🎨',
    'Health & Beauty': '💄',
    'Kitchen & Dining': '🍽️',
    Collectibles: '🧾',
  };
  return map[category] || '♻️';
};

const getStatusInfo = (status) => STATUS_META[status] || { label: 'Scheduled', color: 'warning', icon: '🟡' };

const getDurationLabel = (product, t) => {
  if (product.durationType === 'undetermined') {
    return '∞';
  }

  if (!product.endsAt) {
    return null;
  }

  const end = new Date(product.endsAt).getTime();
  if (Number.isNaN(end)) {
    return null;
  }

  const now = Date.now();
  if (end <= now) {
    return null;
  }

  const diffMs = end - now;
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const remHours = totalHours % 24;
    if (remHours === 0) {
      return days === 1 ? t('roomsSecondHand.duration.dayLeft') : t('roomsSecondHand.duration.daysLeft', { count: days });
    }
    return t('roomsSecondHand.duration.daysHoursLeft', { days, hours: remHours });
  }

  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return t('roomsSecondHand.duration.hoursMinutesLeft', { hours: totalHours, minutes });
};

const RoomsSecondHand = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const borderRadius = `${theme.shape.borderRadius}px`;
  const compactRadius = `${Math.max(theme.shape.borderRadius - 10, 12)}px`;
  const { user } = useAuth();
  const toast = useToast();

  const [secondHandProducts, setSecondHandProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showSellerFormModal, setShowSellerFormModal] = useState(false);
  const [sellerFormData, setSellerFormData] = useState({
    productName: '',
    description: '',
    reservePrice: '',
    condition: 'good',
    category: '',
    images: [],
    defaultOfferInfinite: false,
    defaultOfferDurationHours: '48',
  });
  const [submittingSellerForm, setSubmittingSellerForm] = useState(false);

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [offerAmount, setOfferAmount] = useState('');
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [showTokenFeeModal, setShowTokenFeeModal] = useState(false);

  const fetchSecondHandProducts = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '9',
          status: 'active',
        });

        const data = await apiClient.get(`/api/second-hand-products?${params.toString()}`);

        if (data.success) {
          setSecondHandProducts(Array.isArray(data.data) ? data.data : []);
          setTotalPages(data.pagination?.pages || 1);
          setCurrentPage(data.pagination?.page || page);
        } else {
          console.error('API error:', data.message);
          setSecondHandProducts([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error('Error fetching second-hand products:', error);
        setSecondHandProducts([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchSecondHandProducts(currentPage);
  }, [fetchSecondHandProducts, currentPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSecondHandProducts(currentPage);
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchSecondHandProducts, currentPage]);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleOpenOfferModal = (product) => {
    if (!user) {
      toast.warning(t('roomsSecondHand.toasts.loginRequired'));
      return;
    }

    if (user.id === product.sellerId?._id) {
      toast.warning(t('roomsSecondHand.toasts.ownProduct'));
      return;
    }

    setSelectedProduct(product);
    setOfferAmount('');
    setShowOfferModal(true);
  };

  const handleCloseOfferModal = () => {
    setShowOfferModal(false);
    setSelectedProduct(null);
    setOfferAmount('');
    setSubmittingOffer(false);
  };

  const handleSubmitOffer = async (event) => {
    event.preventDefault();

    if (!selectedProduct || !offerAmount || Number(offerAmount) <= 0) {
      toast.warning(t('roomsSecondHand.toasts.invalidAmount'));
      return;
    }

    try {
      setSubmittingOffer(true);
      const data = await apiClient.post('/api/second-hand-offers', {
        productId: selectedProduct._id,
        offerAmount: Number(offerAmount),
      });

      if (data.success) {
        toast.success(t('roomsSecondHand.toasts.offerSuccess'));
        handleCloseOfferModal();
      } else {
        toast.error(data.message || t('roomsSecondHand.toasts.offerFailed'));
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      const errorMessage = error.response?.data?.message || t('roomsSecondHand.toasts.offerFailedRetry');
      toast.error(errorMessage);
    } finally {
      setSubmittingOffer(false);
    }
  };

  const handleSellerFormSubmit = async (event) => {
    event.preventDefault();

    if (!sellerFormData.productName || !sellerFormData.description || !sellerFormData.category) {
      toast.warning(t('roomsSecondHand.toasts.fillRequired'));
      return;
    }

    // The demanded price is optional, but a negative one is never meaningful.
    if (sellerFormData.reservePrice && Number(sellerFormData.reservePrice) < 0) {
      toast.warning(t('roomsSecondHand.toasts.pricePositive'));
      return;
    }

    try {
      setSubmittingSellerForm(true);
      const formData = new FormData();

      formData.append('productName', sellerFormData.productName);
      formData.append('description', sellerFormData.description);
      formData.append('category', sellerFormData.category);
      formData.append('condition', sellerFormData.condition);

      // Sellers now set a single optional demanded price. The backend still
      // tracks a starting price, so mirror the demanded price into it (0 when
      // the seller left it blank, meaning the listing is open to any offer).
      const demandedPrice = sellerFormData.reservePrice || '0';
      formData.append('reservePrice', demandedPrice);
      formData.append('startingPrice', demandedPrice);

      formData.append('defaultOfferInfinite', String(sellerFormData.defaultOfferInfinite));
      if (!sellerFormData.defaultOfferInfinite) {
        formData.append('defaultOfferDurationHours', sellerFormData.defaultOfferDurationHours || '48');
      }

      sellerFormData.images.forEach((image) => {
        formData.append('images', image);
      });

      const data = await apiClient.post('/api/auction-room-requests', formData);

      if (data.success) {
        toast.success(t('roomsSecondHand.toasts.requestSuccess'));
        setShowSellerFormModal(false);
        setSellerFormData({
          productName: '',
          description: '',
          reservePrice: '',
          condition: 'good',
          category: '',
          images: [],
          defaultOfferInfinite: false,
          defaultOfferDurationHours: '48',
        });
      } else {
        toast.error(data.message || t('roomsSecondHand.toasts.requestFailed'));
      }
    } catch (error) {
      console.error('Error submitting seller form:', error);
      toast.error(t('roomsSecondHand.toasts.requestFailedRetry'));
    } finally {
      setSubmittingSellerForm(false);
    }
  };

  const renderSkeletonGrid = () => (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Grid container spacing={3}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={`skeleton-${index}`}>
            <Card sx={{ borderRadius, p: 2, width: '100%' }}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: compactRadius, mb: 2 }} />
            <Skeleton variant="text" sx={{ fontSize: 24, width: '60%' }} />
            <Skeleton variant="text" sx={{ fontSize: 18, width: '40%' }} />
            <Skeleton variant="rounded" height={80} sx={{ mt: 2, borderRadius: compactRadius }} />
            <Skeleton variant="rounded" height={36} sx={{ mt: 2, borderRadius: compactRadius }} />
          </Card>
        </Grid>
      ))}
    </Grid>
    </Box>
  );

  const renderProductCard = (product) => {
    const statusInfo = getStatusInfo(product.status);
    const durationLabel = getDurationLabel(product, t);
    const conditionColor = CONDITION_CHIP_COLOR[product.condition] || 'default';
    // The reserve price is what the seller is asking for; it is optional, and
    // a listing without one simply invites offers.
    const demandedPrice = Number(product.reservePrice || 0);
    const hasDemandedPrice = demandedPrice > 0;

    return (
      <Card
        key={product._id}
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius,
          border: '1px solid',
          borderColor: 'divider',
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, rgba(255,113,79,.10), rgba(23,21,34,.95) 55%, rgba(118,87,255,.08))'
              : 'linear-gradient(145deg, #ffffff, #faf5f2)',
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.light',
                color: theme.palette.mode === 'dark' ? 'primary.contrastText' : 'primary.main',
                fontSize: 24,
              }}
            >
              {getCategoryIcon(product.category)}
            </Avatar>
          }
          action={
            <Chip
              label={t(`roomsSecondHand.status.${product.status}`, statusInfo.label)}
              color={statusInfo.color}
              size="small"
              variant={statusInfo.color === 'default' ? 'outlined' : 'filled'}
              icon={<span>{statusInfo.icon}</span>}
              sx={{
                '& .MuiChip-icon': {
                  fontSize: 14,
                },
              }}
            />
          }
          title={
            <Tooltip title={product.name || t('roomsSecondHand.card.unnamed')}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {product.name || t('roomsSecondHand.card.unnamed')}
              </Typography>
            </Tooltip>
          }
          subheader={
            <Typography variant="caption" color="text.secondary">
              {product.category || t('roomsSecondHand.card.general')}
            </Typography>
          }
        />
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              position: 'relative',
              borderRadius,
              overflow: 'hidden',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
              pt: '75%',
            }}
          >
            {product.images?.length && product.images[0]?.thumbnailUrl ? (
              <Box
                component="img"
                src={product.images[0].thumbnailUrl}
                alt={product.name || t('roomsSecondHand.card.unnamed')}
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  color: theme.palette.text.secondary,
                  fontSize: 42,
                }}
              >
                {getCategoryIcon(product.category)}
              </Stack>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 48 }}>
            {product.description || t('roomsSecondHand.card.noDescription')}
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              borderRadius,
              p: 2,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 214, 102, 0.08)'
                  : 'rgba(255, 214, 102, 0.18)',
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('roomsSecondHand.card.reservePrice')}
                </Typography>
                {hasDemandedPrice ? (
                  <Typography variant="h6" color="warning.main" fontWeight={700}>
                    {formatPrice(demandedPrice)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t('roomsSecondHand.card.openToOffers', 'Open to offers')}
                  </Typography>
                )}
              </Stack>
              {durationLabel && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('roomsSecondHand.card.duration')}
                  </Typography>
                  <Typography variant="body2" color="info.main" fontWeight={600}>
                    {durationLabel}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Divider sx={{ my: 1 }} />

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('roomsSecondHand.card.condition')}
              </Typography>
              <Chip label={t(`roomsSecondHand.condition.${product.condition}`, getConditionLabel(product.condition))} color={conditionColor} size="small" />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t('roomsSecondHand.card.seller')}
              </Typography>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                <StorefrontIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                {t('roomsSecondHand.card.anonymous')}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
        <CardActions sx={{ p: 3, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            color="warning"
            endIcon={<GavelIcon />}
            onClick={() => handleOpenOfferModal(product)}
            disabled={submittingOffer && selectedProduct?._id === product._id}
            sx={{ fontWeight: 600 }}
          >
            {t('roomsSecondHand.card.makeOffer')}
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <SciFiBackground>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'transparent',
        }}
      >
        <Navbar />
        <Container maxWidth="xl" sx={{ flex: 1, py: { xs: 4, md: 6 }, px: { xs: 2, sm: 3, md: 4 } }}>
          <Stack spacing={5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Stack spacing={1.5} maxWidth={{ xs: '100%', md: '70%' }}>
                <Typography variant="h3" fontWeight={800}>
                  {t('roomsSecondHand.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('roomsSecondHand.subtitle')}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddCircleOutlineIcon />}
                size={isDownMd ? 'medium' : 'large'}
                onClick={() => setShowSellerFormModal(true)}
                sx={{ fontWeight: 700 }}
              >
                {t('roomsSecondHand.requestRoom')}
              </Button>
            </Stack>

            

            {loading ? (
              renderSkeletonGrid()
            ) : secondHandProducts.length ? (
              <Stack spacing={4}>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <Grid
                    container
                    spacing={3}
                  >
                  {secondHandProducts.map((product) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={6}
                        lg={4}
                        key={product._id}
                      >
                      {renderProductCard(product)}
                    </Grid>
                  ))}
                  </Grid>
                </Box>
                {totalPages > 1 && (
                  <Stack alignItems="center">
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      siblingCount={isDownMd ? 0 : 1}
                      boundaryCount={1}
                    />
                  </Stack>
                )}
              </Stack>
            ) : (
              <Card
                elevation={0}
                sx={{
                  borderRadius,
                  border: '1px dashed',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(148, 163, 184, 0.4)',
                  textAlign: 'center',
                  py: 10,
                  px: 6,
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <Typography variant="h2">♻️</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {t('roomsSecondHand.empty.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" maxWidth={420}>
                    {t('roomsSecondHand.empty.subtitle')}
                  </Typography>
                </Stack>
              </Card>
            )}
          </Stack>
        </Container>

        <Dialog
          open={showSellerFormModal}
          onClose={() => setShowSellerFormModal(false)}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle sx={{ pr: 6 }}>
            {t('roomsSecondHand.sellerForm.title')}
            <IconButton
              aria-label="close"
              onClick={() => setShowSellerFormModal(false)}
              sx={{ position: 'absolute', right: 16, top: 16 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} component="form" onSubmit={handleSellerFormSubmit}>
              <Alert severity="info" icon={<ScheduleIcon fontSize="small" />}>
                {t('roomsSecondHand.sellerForm.reviewNotice')}
              </Alert>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('roomsSecondHand.sellerForm.tndNotice')}
                </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                    onClick={() => setShowTokenFeeModal(true)}
                  >
                    {t('roomsSecondHand.sellerForm.viewRules')}
                </Button>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('roomsSecondHand.sellerForm.productName')}
                    value={sellerFormData.productName}
                    onChange={(event) =>
                      setSellerFormData((prev) => ({ ...prev, productName: event.target.value }))
                    }
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('roomsSecondHand.sellerForm.category')}</InputLabel>
                    <Select
                      value={sellerFormData.category}
                      label={t('roomsSecondHand.sellerForm.category')}
                      onChange={(event) =>
                        setSellerFormData((prev) => ({ ...prev, category: event.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>{t('roomsSecondHand.sellerForm.selectCategory')}</em>
                      </MenuItem>
                      {CATEGORY_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                label={t('roomsSecondHand.sellerForm.description')}
                value={sellerFormData.description}
                onChange={(event) =>
                  setSellerFormData((prev) => ({ ...prev, description: event.target.value }))
                }
                multiline
                rows={4}
                required
                fullWidth
              />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('roomsSecondHand.sellerForm.reservePrice')}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={sellerFormData.reservePrice}
                    onChange={(event) =>
                      setSellerFormData((prev) => ({ ...prev, reservePrice: event.target.value }))
                    }
                    helperText={t(
                      'roomsSecondHand.sellerForm.reservePriceHelp',
                      'Optional — leave blank to invite any offer'
                    )}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('roomsSecondHand.sellerForm.condition')}</InputLabel>
                    <Select
                      value={sellerFormData.condition}
                      label={t('roomsSecondHand.sellerForm.condition')}
                      onChange={(event) =>
                        setSellerFormData((prev) => ({ ...prev, condition: event.target.value }))
                      }
                    >
                      <MenuItem value="like_new">{t('roomsSecondHand.condition.like_new')}</MenuItem>
                      <MenuItem value="excellent">{t('roomsSecondHand.condition.excellent')}</MenuItem>
                      <MenuItem value="good">{t('roomsSecondHand.condition.good')}</MenuItem>
                      <MenuItem value="fair">{t('roomsSecondHand.condition.fair')}</MenuItem>
                      <MenuItem value="poor">{t('roomsSecondHand.condition.poor')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box>
                <Button variant="outlined" component="label">
                  {t('roomsSecondHand.sellerForm.uploadImages')}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(event) =>
                      setSellerFormData((prev) => ({
                        ...prev,
                        images: Array.from(event.target.files ?? []),
                      }))
                    }
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('roomsSecondHand.sellerForm.uploadHelp')}
                </Typography>
                {sellerFormData.images.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {t('roomsSecondHand.sellerForm.filesSelected', { count: sellerFormData.images.length })}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('roomsSecondHand.sellerForm.listingVisibility')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('roomsSecondHand.sellerForm.listingVisibilityHelp')}
                </Typography>
                <RadioGroup
                  value={sellerFormData.defaultOfferInfinite ? 'infinite' : 'countdown'}
                  onChange={(event) =>
                    setSellerFormData((prev) => ({
                      ...prev,
                      defaultOfferInfinite: event.target.value === 'infinite',
                    }))
                  }
                >
                  <FormControlLabel
                    value="infinite"
                    control={<Radio />}
                    label={t('roomsSecondHand.sellerForm.infinite')}
                  />
                  <FormControlLabel
                    value="countdown"
                    control={<Radio />}
                    label={t('roomsSecondHand.sellerForm.countdown')}
                  />
                </RadioGroup>
                {!sellerFormData.defaultOfferInfinite && (
                  <TextField
                    label={t('roomsSecondHand.sellerForm.defaultDuration')}
                    type="number"
                    inputProps={{ min: 1, max: 720 }}
                    value={sellerFormData.defaultOfferDurationHours}
                    onChange={(event) =>
                      setSellerFormData((prev) => ({
                        ...prev,
                        defaultOfferDurationHours: event.target.value,
                      }))
                    }
                    fullWidth
                    sx={{ mt: 2, maxWidth: 240 }}
                    helperText={t('roomsSecondHand.sellerForm.maxDuration')}
                    error={Number(sellerFormData.defaultOfferDurationHours || 0) > 720}
                  />
                )}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  onClick={() => setShowSellerFormModal(false)}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  type="submit"
                  disabled={submittingSellerForm}
                >
                  {submittingSellerForm ? t('roomsSecondHand.sellerForm.submitting') : t('roomsSecondHand.sellerForm.submitRequest')}
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

  <TokenFeeInfoModal open={showTokenFeeModal} onClose={() => setShowTokenFeeModal(false)} />

        <Dialog
          open={showOfferModal && Boolean(selectedProduct)}
          onClose={handleCloseOfferModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            {t('roomsSecondHand.offerModal.title')}
            <IconButton
              aria-label="close"
              onClick={handleCloseOfferModal}
              sx={{ position: 'absolute', right: 16, top: 16 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedProduct && (
              <Stack spacing={3}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h3" component="span">
                      {getCategoryIcon(selectedProduct.category)}
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {selectedProduct.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('roomsSecondHand.offerModal.reservePrice', { price: formatPrice(selectedProduct.reservePrice) })}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Box component="form" onSubmit={handleSubmitOffer}>
                  <Stack spacing={3}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('roomsSecondHand.offerModal.lockNotice')}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<InfoOutlinedIcon fontSize="small" />}
                        onClick={() => setShowTokenFeeModal(true)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                          {t('roomsSecondHand.offerModal.viewRules')}
                      </Button>
                    </Stack>
                    <TextField
                      label={t('roomsSecondHand.offerModal.offerLabel')}
                      type="number"
                      inputProps={{ min: 1, step: 0.01 }}
                      value={offerAmount}
                      onChange={(event) => setOfferAmount(event.target.value)}
                      required
                      fullWidth
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Button variant="outlined" fullWidth onClick={handleCloseOfferModal}>
                        {t('cancel')}
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        endIcon={<GavelIcon />}
                        type="submit"
                        fullWidth
                        disabled={submittingOffer || !offerAmount}
                      >
                        {submittingOffer ? t('roomsSecondHand.offerModal.submitting') : t('roomsSecondHand.offerModal.submitOffer')}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </SciFiBackground>
  );
};

export default RoomsSecondHand;
